import React, { FC, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MenuPrimary } from '@chownow/cn-web-components';
import Restaurant from 'types/restaurant';
import { OnboardingWizardPages } from 'types/progress';
import { ONB_WIZARD_INTRO, ONE_DASHBOARD_BASE_URL } from 'helpers/constants';
import { CompanyContext } from 'context/CompanyContext';

import { ReactComponent as PictoMenuIcon } from 'images/pictograms/picto-menu-md.svg';
import { ReactComponent as PictoClipboardIcon } from 'images/pictograms/picto-clipboard-md.svg';
import { ReactComponent as PictoHoursIcon } from 'images/pictograms/picto-hours-md.svg';
import { ReactComponent as PictoImageIcon } from 'images/pictograms/picto-image-md.svg';
import { ReactComponent as PictoPaymentIcon } from 'images/pictograms/picto-payment-md.svg';
import { ReactComponent as PictoOrderDirectIcon } from 'images/pictograms/picto-order-direct-md.svg';
import { ReactComponent as PictoPromoIcon } from 'images/pictograms/picto-promo-md.svg';

import OnboardingContainer from '../OnboardingContainer';
import {
  MissingDetailsContainer,
  MissingDetailsHeading,
  MissingDetailsSubhead,
  DetailsContainer,
  Step,
  StyledButton,
} from './styles';

interface MissingStepsProps {
  missingWizardDetails: string[];
  restaurant: Restaurant;
}

const MissingSteps: FC<MissingStepsProps> = ({
  missingWizardDetails,
  restaurant,
}) => {
  const { company } = useContext(CompanyContext);
  const navigate = useNavigate();
  const onboardingUrlPrefix = `/restaurant/${restaurant?.id}/onboarding/`;

  useEffect(() => {
    if (missingWizardDetails.length === 0) {
      navigate(`/restaurant/${restaurant?.id}/onboarding/completed`);
    }
  }, [missingWizardDetails, navigate, restaurant?.id]);

  const handleShareMenuClick = () => {
    window.location.href = `${onboardingUrlPrefix}share-menu?origin=missing-steps`;
  };

  const handleSalesTaxClick = () => {
    window.location.href = `${onboardingUrlPrefix}sales-tax?origin=missing-steps`;
  };

  const handleRestaurantHoursClick = () => {
    window.location.href = `${onboardingUrlPrefix}restaurant-hours?origin=missing-steps`;
  };

  const handleWebsiteAccessClick = () => {
    window.location.href = `${onboardingUrlPrefix}website-access?origin=missing-steps`;
  };

  const handleFoodAndLogoClick = () => {
    window.location.href = `${onboardingUrlPrefix}logo-and-food-image?origin=missing-steps`;
  };

  const handlePromoClick = () => {
    window.location.href = `${onboardingUrlPrefix}promotion?origin=missing-steps`;
  };

  const handlePaymentProcessingClick = () => {
    window.location.href = `${onboardingUrlPrefix}payment-processing?origin=missing-steps`;
  };

  const handleExploreClick = () => {
    if (company?.should_redirect_to_portal) {
      window.open(ONE_DASHBOARD_BASE_URL, '_blank');
    } else {
      navigate(`/restaurant/${restaurant?.id}`);
    }
  };

  return (
    <OnboardingContainer>
      <MissingDetailsContainer>
        <MissingDetailsHeading>
          {ONB_WIZARD_INTRO.missingStepsTitle}
        </MissingDetailsHeading>
        <MissingDetailsSubhead>
          {ONB_WIZARD_INTRO.missingStepsDesc}
        </MissingDetailsSubhead>
        <DetailsContainer>
          {missingWizardDetails.map(step => {
            let returnedDetail = null;

            if (step === OnboardingWizardPages.SHARED_MENU) {
              returnedDetail = (
                <Step key="share-your-menu">
                  <MenuPrimary
                    handleClick={handleShareMenuClick}
                    icon={PictoMenuIcon}
                    label="Share your menu"
                    showArrow
                  />
                </Step>
              );
            } else if (step === OnboardingWizardPages.TAX_RATE) {
              returnedDetail = (
                <Step key="sales-tax">
                  <MenuPrimary
                    handleClick={handleSalesTaxClick}
                    icon={PictoClipboardIcon}
                    label="Sales tax rate"
                    showArrow
                  />
                </Step>
              );
            } else if (step === OnboardingWizardPages.SCHEDULE) {
              returnedDetail = (
                <Step key="restaurant-hours">
                  <MenuPrimary
                    handleClick={handleRestaurantHoursClick}
                    icon={PictoHoursIcon}
                    label="Restaurant hours"
                    showArrow
                  />
                </Step>
              );
            } else if (step === OnboardingWizardPages.WEBSITE_ACCESS) {
              returnedDetail = (
                <Step key="website-access">
                  <MenuPrimary
                    handleClick={handleWebsiteAccessClick}
                    icon={PictoOrderDirectIcon}
                    label="Website access"
                    showArrow
                  />
                </Step>
              );
            } else if (step === OnboardingWizardPages.LOGO_AND_FOOD_IMAGE) {
              returnedDetail = (
                <Step key="food and logo">
                  <MenuPrimary
                    handleClick={handleFoodAndLogoClick}
                    icon={PictoImageIcon}
                    label="Logo and image"
                    showArrow
                  />
                </Step>
              );
            } else if (step === OnboardingWizardPages.PROMOTION) {
              returnedDetail = (
                <Step key="promotion">
                  <MenuPrimary
                    handleClick={handlePromoClick}
                    icon={PictoPromoIcon}
                    label="Promotion"
                    showArrow
                  />
                </Step>
              );
            } else if (step === OnboardingWizardPages.STRIPE_VERIFICATION) {
              returnedDetail = (
                <Step key="payment-processing">
                  <MenuPrimary
                    handleClick={handlePaymentProcessingClick}
                    icon={PictoPaymentIcon}
                    label="Payment processing"
                    showArrow
                  />
                </Step>
              );
            }

            return returnedDetail;
          })}
        </DetailsContainer>
        <StyledButton
          label="Explore Your Dashboard"
          onClick={handleExploreClick}
        />
      </MissingDetailsContainer>
    </OnboardingContainer>
  );
};

export default MissingSteps;
